<template>
  <div
    :id="id"
    class="w-100">
    <valid-field
      v-slot="{ errors }"
      :name="id"
      :model-value="innerValue"
      :rules="rules">
      <qx-multiselect
        v-model="innerValue"
        :options="options"
        :label="label"
        :track-by="trackBy"
        :preselect-first="preselectFirst"
        :searchable="searchable"
        :close-on-select="closeOnSelect"
        :placeholder="string.textPlaceholder"
        :disabled="disabled"
        :hide-selected="hideSelected"
        :group-values="groupValues"
        :group-label="groupLabel"
        :group-select="groupSelect"
        :no-result="string.textNoResult"
        :max-height="maxHeight"
        :should-sort="shouldSort"
        :is-error="errors.length > 0"
        @change="onChange">
        <template #singleLabel="{ option }">
          <span
            class="text-truncate"
            :title="option.text">{{ option.text }}</span>
        </template>
        <template #option="{ option }">
          <slot
            name="option"
            :option="option">
            <span
              v-if="isGroupLabel(option)"
              class="multiselect-group-label">{{ optionText(option) }} </span>
            <span
              v-else-if="optionText(option)"
              :value="optionText(option)">{{ optionText(option) }} </span>
          </slot>
        </template>
      </qx-multiselect>
      <div class="after-input">
        <slot name="after-input">
          <span
            v-if="errors.length > 0"
            :id="`${id}-validator-error`"
            class="validator-error">
            {{ errors[0] }}
          </span>
        </slot>
      </div>
    </valid-field>
  </div>
</template>

<script>
import QxMultiselect from '@/elements/QxMultiselect.vue';
import { Field as ValidField } from 'vee-validate';

export default {
  name: 'QxSelect',
  components: {
    ValidField,
    QxMultiselect,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    modelValue: {
      type: [String, Array, Object],
      default: '',
    },
    options: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: 'text',
    },
    trackBy: {
      type: String,
      default: 'value',
    },
    preselectFirst: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    closeOnSelect: {
      type: Boolean,
      default: true,
    },
    hideSelected: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    noErrorBlock: {
      type: Boolean,
      default: false,
    },
    groupValues: {
      type: String,
      default: '',
    },
    groupLabel: {
      type: String,
      default: '',
    },
    groupSelect: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'lazy',
      validator: (value) => ['aggressive', 'passive', 'lazy', 'eager'].includes(value),
    },
    rules: {
      type: [String, Object],
      default: '',
    },
    onChange: {
      type: Function,
      default: () => {},
    },
    maxHeight: {
      type: Number,
      default: 150,
    },
    shouldSort: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:modelValue'],
  computed: {
    string() {
      return {
        textNoResult: this.$gettext('No results found'),
        textPlaceholder:
          this.placeholder || (this.searchable && this.$gettext('Type to search')) || this.$gettext('Choose an option'),
      };
    },
    innerValue: {
      get() {
        if (this.groupValues.length > 0) {
          return this.modelValue;
        }
        return this.options.find((option) => option[this.trackBy] === this.modelValue) || '';
      },
      set(option) {
        if (!option) {
          return;
        }
        const value = this.groupValues.length > 0 ? [option] : option[this.trackBy];
        this.$emit('update:modelValue', value);
      },
    },
  },
  methods: {
    isGroupLabel(option) {
      return this.groupLabel && option.$groupLabel?.length > 0;
    },
    optionText(option) {
      if (this.isGroupLabel(option)) {
        return option.$groupLabel;
      }
      if (this.groupLabel && option.$groupLabel?.length === 0) {
        return '';
      }
      return option[this.label];
    },
  },
};
</script>

<style lang="scss" scoped>
.multiselect-group-label {
  padding: 3px 12px;
  font-size: 10px;
  text-transform: uppercase;
}
</style>
